import React from 'react';

import {withRouter, WithRouterProps} from 'react-router';

import {DashboardSidebarFoldersList} from 'app/components/general/DashboardPage/DashboardSidebarFoldersList';
import MainCol from 'app/components/general/MainCol';
import {StyledSidebarDIV} from 'app/components/general/Sidebar';
import {DashboardItemsCounter} from 'app/general/dashboards/DashboardItemsCounter';
import {BootstrapMinMediumMediaQuery} from 'app/general/mediaqueries';
import {DocumentsNotificationsProvider} from 'app/notifly/components/DocumentsNotificationsProvider';
import {PresenceSubscriber} from 'app/presence/components/PresenceSubscriber';
import {useSlimTasks} from 'app/tasks/api';
import {useAllTasksDashboardFilters} from 'app/tasks/dashboard-utils';
import {setLastUsedTasksDashboard} from 'app/tasks/slice';
import {ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import {CompactTasksList} from './CompactTasksList';
import {TasksFilterbar} from './TasksFilterbar';
import {TasksLinkPagination} from './TasksLinkPagination';

import type {TaskDashboardLocationParamsType} from 'app/tasks/types';

interface Props
  extends WithRouterProps<
    Record<string, unknown>,
    TaskDashboardLocationParamsType
  > {
  isManager: boolean;
  userTimezone: string;
  toggleAssignTaskModal: () => void;
}

export const AllTasksList = withRouter(
  ({
    isManager,
    toggleAssignTaskModal,
    location,
    userTimezone,
    router,
  }: Props): JSX.Element | null => {
    const actions = useAsyncActions({
      setLastUsedDashboard: setLastUsedTasksDashboard,
    });

    const {redirecting, fetchParams, viewingAll} = useAllTasksDashboardFilters(
      location,
      router
    );

    let {currentData: tasks} = useSlimTasks(fetchParams, {
      skip: redirecting,
    });

    React.useEffect(() => {
      actions.setLastUsedDashboard('all');
    }, []);

    function resetFilters() {
      router.replace({pathname: location.pathname});
    }

    return redirecting ? null : (
      <div className="row">
        <BootstrapMinMediumMediaQuery>
          <StyledSidebarDIV>
            <DashboardSidebarFoldersList />
          </StyledSidebarDIV>
        </BootstrapMinMediumMediaQuery>
        <MainCol>
          <TasksFilterbar
            resetFilters={resetFilters}
            viewingAll={viewingAll}
            userTimezone={userTimezone}
          />
          {tasks != null ? (
            <DashboardItemsCounter
              text={ngt('%s task', '%s tasks', tasks.count)}
              showSearchHelp={!!location.query.search}
            />
          ) : null}
          <DocumentsNotificationsProvider
            contentType="taskinstance"
            documentsIds={
              tasks?.results.map((p) => p.id).filter((id) => id) ?? []
            }
          >
            {(tasksNotifications) =>
              tasks != null ? (
                <CompactTasksList
                  tasks={tasks}
                  notifications={tasksNotifications}
                  canCreate={isManager}
                  showAssignTaskModal={toggleAssignTaskModal}
                  userTimezone={userTimezone}
                />
              ) : null
            }
          </DocumentsNotificationsProvider>
          <PresenceSubscriber
            contentType="taskinstance"
            objectsIds={
              tasks?.results.map((p) => p.id).filter((id) => id) ?? []
            }
            stealth={true}
          />
          {tasks != null ? (
            <TasksLinkPagination
              params={location.query}
              userTimezone={userTimezone}
              response={tasks}
            />
          ) : null}
        </MainCol>
      </div>
    );
  }
);
